import React, { useState, useEffect } from 'react';
import { SHOW_ADD_TASK_BUTTON } from '../../../../../shared/constants/featureFlags';
import { DND_SENTINEL_BEFORE_FIRST, DND_SENTINEL_AFTER_LAST } from '../../../utils/dndConstants';
import { isSequentialTask } from '../../../utils/sequentialTasks';
import ListDropSentinel from './ListDropSentinel';
import './TaskList.css';

const PLACEHOLDER_REACT_KEY = 'placeholder_client_key';

function TaskList({
  tasks,
  expandedTasks,
  editingTaskId,
  editingDueDateTaskId,
  blockingTasksHighlight,
  filter,
  timeFilter,
  editingElementRef,
  dateInputCallbackRef,
  onToggleExpand,
  onCheckboxChange,
  onSetEditingTaskId,
  onSetEditingDueDateTaskId,
  onUpdateTaskDueDate,
  onTaskTextClick,
  onTaskTextFocus,
  onTaskContentClick,
  onTaskTextBlur,
  onTaskTextKeyDown,
  onTaskTextPaste,
  onBlockedIconMouseEnter,
  onBlockedIconMouseLeave,
  renderTask,
  placeholderStatus = 'incomplete',
  placeholderDueDate = null,
  placeholderIsLaterDate = false,
  placeholderHasContent = false,
  onAddTaskAfterRoot,
  showPlaceholderWhenEmpty = true,
  disableSequentialAdd = false
}) {
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsMobileViewport(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const placeholderTask = tasks.length === 0 ? {
    id: 'placeholder',
    _reactKey: PLACEHOLDER_REACT_KEY,
    text: '',
    status: placeholderStatus,
    parent_task_id: null,
    due_date: placeholderDueDate ?? null,
    is_later_date: placeholderIsLaterDate === true,
    order: null,
    subtasks: []
  } : null;

  const isPlaceholderFocused = editingTaskId === PLACEHOLDER_REACT_KEY;
  const showAddTaskBelowPlaceholder =
    isPlaceholderFocused &&
    placeholderHasContent &&
    SHOW_ADD_TASK_BUTTON &&
    onAddTaskAfterRoot;

  if (tasks.length === 0 && placeholderTask && showPlaceholderWhenEmpty) {
    return (
      <ul className="task-list">
        {renderTask(placeholderTask)}
        {showAddTaskBelowPlaceholder && (
          <li className={`add-task-row add-task-row-root${isMobileViewport ? ' add-task-row-placeholder-mobile' : ''}`}>
            <button
              type="button"
              className="add-task-button add-task-button--hint"
              disabled
            >
              Press <span className="add-task-enter-icon" aria-hidden="true">&#x23CE;</span> to add task
            </button>
          </li>
        )}
      </ul>
    );
  }

  if (tasks.length === 0) {
    if (SHOW_ADD_TASK_BUTTON && onAddTaskAfterRoot) {
      return (
        <ul className="task-list">
          <li className="add-task-row add-task-row-root add-task-row-empty-section">
            <button
              type="button"
              className="add-task-button"
              onClick={() => onAddTaskAfterRoot(false)}
            >
              Add task...
            </button>
          </li>
        </ul>
      );
    }
    return <div className="empty-message">No tasks yet</div>;
  }

  const lastTask = tasks[tasks.length - 1];
  const isEditingLastTask = lastTask && editingTaskId === lastTask._reactKey;
  const isLastTaskParent = lastTask && lastTask.subtasks && lastTask.subtasks.length > 0;
  const isLastTaskSequential = lastTask && isSequentialTask(lastTask);
  const allowSequentialAdd = !disableSequentialAdd && isLastTaskSequential;

  return (
    <ul className="task-list">
      <ListDropSentinel id={DND_SENTINEL_BEFORE_FIRST} position="before" />
      {tasks.map(task => renderTask(task))}
      <ListDropSentinel id={DND_SENTINEL_AFTER_LAST} position="after" />
      {SHOW_ADD_TASK_BUTTON && onAddTaskAfterRoot && tasks.length > 0 && (
        <li className={`add-task-row add-task-row-root${isLastTaskParent ? ' add-task-row-after-parent' : ''}${isLastTaskSequential ? ' add-task-row--dual' : ''}`}>
          {isEditingLastTask ? (
            <button
              type="button"
              className="add-task-button add-task-button--hint"
              disabled
            >
              Press <span className="add-task-enter-icon" aria-hidden="true">&#x23CE;</span> to add {isLastTaskSequential ? 'sequential ' : ''}task
            </button>
          ) : allowSequentialAdd ? (
            <span className="add-task-row__options">
              <button
                type="button"
                className="add-task-button add-task-button--inline"
                onClick={() => onAddTaskAfterRoot(true)}
              >
                Add sequential task
              </button>
              <span className="add-task-row__separator"> or </span>
              <button
                type="button"
                className="add-task-button add-task-button--inline"
                onClick={() => onAddTaskAfterRoot(false)}
              >
                regular task...
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="add-task-button"
              onClick={() => onAddTaskAfterRoot(false)}
            >
              Add task...
            </button>
          )}
        </li>
      )}
    </ul>
  );
}

export default TaskList;

